import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import FormField from '@/components/atoms/FormField';
import { Combobox } from '@/components/molecules/Combobox';
import type { ClarifyCatalog, ClarifyCatalogItem } from '@/domain/providerConfig';

import { catalogLabelLines } from './catalogLabels';

import styles from './ClarifyItemPicker.module.scss';

export interface ClarifyItemPickerProps {
    items: ClarifyCatalog['items'];
    value: string;
    onChange: (id: string) => void;
    label: string;
}

/** A catalog holds up to 1000 items; a short list keeps the options readable. */
const SHOWN_MAX = 50;

function matches(item: ClarifyCatalogItem, query: string): boolean {
    if (query === '') return true;
    return [item.name, item.id, ...item.labels].some((text) => text.toLowerCase().includes(query));
}

/** A search over the Clarify catalog by name, label or id that sets the item id. */
function ClarifyItemPicker({ items, value, onChange, label }: ClarifyItemPickerProps) {
    const { t } = useTranslation();
    // React's ids hold colons, which break the `#id` selectors jsdom and some libraries build.
    const id = useId().replaceAll(':', '');
    const [query, setQuery] = useState('');

    const shown = useMemo(() => {
        const needle = query.trim().toLowerCase();
        return items.filter((item) => matches(item, needle)).slice(0, SHOWN_MAX);
    }, [items, query]);
    const selected = items.find((item) => item.id === value) ?? null;
    const labelLines = useMemo(() => catalogLabelLines(items), [items]);

    return (
        <FormField label={label} htmlFor={id}>
            <Combobox
                id={id}
                aria-label={label}
                spellCheck={false}
                query={query}
                onQueryChange={setQuery}
                options={shown}
                getKey={(item) => item.id}
                renderOption={(item) => (
                    <span className={styles.row}>
                        <span className={styles.optionName}>{item.name}</span>
                        {item.engUnit && <span className={styles.unit}>{item.engUnit}</span>}
                        {labelLines.get(item.id) && (
                            <span className={styles.labels}>{labelLines.get(item.id)}</span>
                        )}
                        <span className={styles.itemId}>{item.id}</span>
                    </span>
                )}
                onSelect={(item) => {
                    onChange(item.id);
                    setQuery('');
                }}
                status={shown.length === 0 ? t('providerConfig.pens.noItemMatches') : ''}
            />
            {selected && (
                <p className={styles.picked}>
                    <span className={styles.optionName}>{selected.name}</span>
                    {selected.engUnit && <span className={styles.unit}>{selected.engUnit}</span>}
                    {labelLines.get(selected.id) && (
                        <span className={styles.labels}>{labelLines.get(selected.id)}</span>
                    )}
                </p>
            )}
        </FormField>
    );
}

export default ClarifyItemPicker;
export { ClarifyItemPicker };
